/**
 * UI preferences — configs statiques (options, limites, defaults).
 *
 * @remarks
 * Repris 1:1 du legacy `sass front/src/types/uiPreferences.ts` +
 * `sass front/src/config/uiPreferences.ts`. Tree-shakable, hors store Zustand.
 */

import { CODE_THEMES } from './code-themes';

// ── Types ───────────────────────────────────────────────────────────────

export type MarkStyle = 'sketch' | 'classic' | 'none';

export type FontFamily =
  | 'system'
  | 'serif'
  | 'mono'
  | 'inter'
  | 'roboto'
  | 'open-sans'
  | 'lato'
  | 'poppins'
  | 'montserrat';

export type AnimationSpeed = 'none' | 'reduced' | 'normal';
export type ContentLayout = 'centered' | 'stretched';

export interface UIPreferences {
  // Typography
  fontFamily: FontFamily;
  fontSize: number; // 10-24 px
  codeTheme: string;
  lineHeight: number; // 1-2
  letterSpacing: number; // -0.05 to 0.1 em

  // Layout
  compactMode: boolean;
  contentLayout: ContentLayout;

  // Animations
  animationSpeed: AnimationSpeed;

  // Marks (sketch overlays)
  markHighlightColor: string;
  markUnderlineColor: string;
  separatorColor: string;
  emptyHintColor: string;
  annotationBracketColor: string;

  markHighlightStyle: MarkStyle;
  markUnderlineStyle: MarkStyle;
  separatorStyle: MarkStyle;
  annotationBracketStyle: MarkStyle;

  // Diagram modal
  diagramModalSize: number; // 0.5-1

  // Code blocks
  codeBlocksCollapsedByDefault: boolean;
  codeBlocksShowLineNumbers: boolean;
}

export const DEFAULT_UI_PREFERENCES: UIPreferences = {
  fontFamily: 'system',
  fontSize: 15,
  codeTheme: 'monokai',
  lineHeight: 1.4,
  letterSpacing: 0,
  compactMode: false,
  contentLayout: 'stretched',
  animationSpeed: 'normal',
  markHighlightColor: '',
  markUnderlineColor: '',
  separatorColor: '',
  emptyHintColor: '',
  annotationBracketColor: '',
  markHighlightStyle: 'sketch',
  markUnderlineStyle: 'sketch',
  separatorStyle: 'sketch',
  annotationBracketStyle: 'sketch',
  diagramModalSize: 0.8,
  codeBlocksCollapsedByDefault: false,
  codeBlocksShowLineNumbers: false,
};

export const FONT_SIZE_LIMITS = { MIN: 10, MAX: 24 } as const;
export const LINE_HEIGHT_LIMITS = { MIN: 1, MAX: 2 } as const;
export const LETTER_SPACING_LIMITS = { MIN: -0.05, MAX: 0.1 } as const;

// ── Options listées ────────────────────────────────────────────────────

export interface FontFamilyOption {
  readonly id: FontFamily;
  readonly label: string;
  readonly cssValue: string;
  readonly preview: string;
}

export const FONT_FAMILY_OPTIONS: readonly FontFamilyOption[] = [
  {
    id: 'system',
    label: 'Police système',
    cssValue: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    preview: 'Portez ce vieux whisky au juge blond',
  },
  {
    id: 'inter',
    label: 'Inter',
    cssValue: '"Inter", system-ui, sans-serif',
    preview: 'Portez ce vieux whisky au juge blond',
  },
  {
    id: 'serif',
    label: 'Serif',
    cssValue: 'Georgia, "Times New Roman", serif',
    preview: 'Portez ce vieux whisky au juge blond',
  },
  {
    id: 'mono',
    label: 'Chasse fixe',
    cssValue: 'ui-monospace, "SF Mono", Monaco, monospace',
    preview: 'Portez ce vieux whisky au juge blond',
  },
  {
    id: 'roboto',
    label: 'Roboto',
    cssValue: '"Roboto", system-ui, sans-serif',
    preview: 'Portez ce vieux whisky au juge blond',
  },
  {
    id: 'open-sans',
    label: 'Open Sans',
    cssValue: '"Open Sans", system-ui, sans-serif',
    preview: 'Portez ce vieux whisky au juge blond',
  },
  {
    id: 'lato',
    label: 'Lato',
    cssValue: '"Lato", system-ui, sans-serif',
    preview: 'Portez ce vieux whisky au juge blond',
  },
  {
    id: 'poppins',
    label: 'Poppins',
    cssValue: '"Poppins", system-ui, sans-serif',
    preview: 'Portez ce vieux whisky au juge blond',
  },
  {
    id: 'montserrat',
    label: 'Montserrat',
    cssValue: '"Montserrat", system-ui, sans-serif',
    preview: 'Portez ce vieux whisky au juge blond',
  },
];

export interface AnimationSpeedOption {
  readonly id: AnimationSpeed;
  readonly label: string;
  readonly multiplier: number;
}

export const ANIMATION_SPEED_OPTIONS: readonly AnimationSpeedOption[] = [
  { id: 'none', label: 'Aucune animation', multiplier: 0 },
  { id: 'reduced', label: 'Animations réduites', multiplier: 0.5 },
  { id: 'normal', label: 'Vitesse normale', multiplier: 1 },
];

// ── Code themes ────────────────────────────────────────────────────────
//
// Les options dérivent de la palette canonique `CODE_THEMES` (10 thèmes ×
// 10 couleurs de syntax highlighting). On expose ici un sous-shape minimal
// (id + label + background) consommé par le picker UI dans
// `SettingsFormView`. Le push effectif des couleurs vers le DOM passe par
// `applyPreferencesToDocument` qui appelle `getCodeTheme(id)`.

export interface CodeThemeOption {
  readonly id: string;
  readonly label: string;
  /** Couleur de fond du thème — pour preview swatch dans le picker. */
  readonly background: string;
}

export const CODE_THEME_OPTIONS: readonly CodeThemeOption[] = CODE_THEMES.map((t) => ({
  id: t.id,
  label: t.name,
  background: t.colors.background,
}));

// ── Mark colors (Excalidraw-style overlays) ─────────────────────────────

export interface MarkColorOption {
  readonly label: string;
  readonly value: string;
  readonly swatch: string;
}

export const MARK_HIGHLIGHT_OPTIONS: readonly MarkColorOption[] = [
  { label: 'Vert', value: 'rgba(134, 239, 172, 0.42)', swatch: '#86efac' },
  { label: 'Jaune', value: 'rgba(250, 204, 21, 0.48)', swatch: '#facc15' },
  { label: 'Bleu', value: 'rgba(147, 197, 253, 0.48)', swatch: '#93c5fd' },
  { label: 'Rose', value: 'rgba(249, 168, 212, 0.48)', swatch: '#f9a8d4' },
  { label: 'Orange', value: 'rgba(253, 186, 116, 0.48)', swatch: '#fdba74' },
];

export const MARK_UNDERLINE_OPTIONS: readonly MarkColorOption[] = [
  { label: 'Violet', value: '#8b5cf6', swatch: '#8b5cf6' },
  { label: 'Bleu', value: '#3b82f6', swatch: '#3b82f6' },
  { label: 'Rouge', value: '#ef4444', swatch: '#ef4444' },
  { label: 'Vert', value: '#10b981', swatch: '#10b981' },
  { label: 'Orange', value: '#f59e0b', swatch: '#f59e0b' },
];

export const SEPARATOR_COLOR_OPTIONS: readonly MarkColorOption[] = [
  { label: 'Violet', value: '#8b5cf6', swatch: '#8b5cf6' },
  { label: 'Bleu', value: '#3b82f6', swatch: '#3b82f6' },
  { label: 'Ardoise', value: '#64748b', swatch: '#64748b' },
  { label: 'Rose', value: '#ec4899', swatch: '#ec4899' },
  { label: 'Ambre', value: '#f59e0b', swatch: '#f59e0b' },
];

export const EMPTY_HINT_COLOR_OPTIONS: readonly MarkColorOption[] = [
  { label: 'Ardoise', value: '#64748b', swatch: '#64748b' },
  { label: 'Violet', value: '#8b5cf6', swatch: '#8b5cf6' },
  { label: 'Bleu', value: '#3b82f6', swatch: '#3b82f6' },
  { label: 'Vert', value: '#10b981', swatch: '#10b981' },
  { label: 'Rose', value: '#ec4899', swatch: '#ec4899' },
];

export const ANNOTATION_BRACKET_COLOR_OPTIONS: readonly MarkColorOption[] = [
  { label: 'Violet', value: '#8b5cf6', swatch: '#8b5cf6' },
  { label: 'Ardoise', value: '#64748b', swatch: '#64748b' },
  { label: 'Bleu', value: '#3b82f6', swatch: '#3b82f6' },
  { label: 'Vert', value: '#10b981', swatch: '#10b981' },
  { label: 'Ambre', value: '#f59e0b', swatch: '#f59e0b' },
  { label: 'Rose', value: '#ec4899', swatch: '#ec4899' },
];
